import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * A side sheet: the composer that slides in from the right.
 *
 * ── WHY ONE COMPONENT ──
 * Eleven pages each wrote their own - the same fixed scrim, the same
 * `max-w-md` panel, the same header - and each one had the same four
 * phone bugs, because the phone was not in the room when they were written:
 *
 *   - `h-full` of a `fixed inset-0` box is the LAYOUT viewport. In a browser
 *     tab on iOS that is taller than what is visible while the address bar
 *     is up, so the sheet's bottom - where Save lives - was under the bar.
 *     `h-dvh` is the height actually on screen, and follows the bar.
 *   - Save and Cancel sat at the END of the scrolling form. On a phone that
 *     is a screen and a half down, past the tag picker, and a person who has
 *     filled in the amount does not know the form has a bottom. The footer
 *     is now a fixed strip under the fields, padded above the home indicator.
 *   - Scrolling to the end of the form kept scrolling the page underneath
 *     (the browser's scroll chaining). `overscroll-contain` on the body stops
 *     the sheet from handing its scroll to the page behind the scrim.
 *   - The scrim closed nothing on half the sheets and everything on the
 *     other half. Every sheet now closes on a tap on the scrim, and none
 *     closes on a drag that STARTS inside and ends outside - `pointerdown`
 *     is checked against the scrim itself, so selecting text in a field and
 *     overshooting does not throw the form away.
 *
 * On a laptop it is still a 448px panel on the right. On a phone it is the
 * whole screen, because `max-w-md` is wider than the screen; nothing has to
 * decide which one it is.
 *
 * `role="dialog"` is on the PANEL, which is the thing with the label, and
 * `aria-modal` says the page behind it is out of the reading order. Tests
 * find it as `getByRole('dialog', { name })`, as they did before.
 */
export function Sheet({
  label,
  labelledBy,
  onClose,
  className,
  children,
  wide,
  testId,
}: {
  /** The accessible name. One of `label` or `labelledBy` is required. */
  label?: string;
  labelledBy?: string;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  /** The payment sheet lays two columns; everything else is `max-w-md`. */
  wide?: boolean;
  testId?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-foreground/20 animate-fade-in"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-labelledby={labelledBy}
        data-testid={testId}
        className={cn(
          'flex h-dvh w-full flex-col border-l border-border bg-card shadow-xl',
          wide ? 'max-w-xl' : 'max-w-md',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The title row and the close button.
 *
 * Padded on top by the safe-area inset, because on a phone the sheet covers
 * the whole screen INCLUDING the notch, and a title under the notch is a
 * title you cannot read.
 */
export function SheetHeader({
  title,
  titleId,
  onClose,
  children,
}: {
  title: React.ReactNode;
  titleId?: string;
  onClose: () => void;
  /** Anything beside the close button - a "Delete" for an edit sheet. */
  children?: React.ReactNode;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-5 py-3 pt-[max(0.75rem,var(--safe-top))]">
      <h2 id={titleId} className="min-w-0 truncate text-sm font-semibold">
        {title}
      </h2>
      <div className="flex shrink-0 items-center gap-1">
        {children}
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/**
 * The form, laid out as a column so that the body scrolls and the footer
 * stays. A plain `<form>` with `flex-1` would scroll as a whole and take the
 * buttons with it.
 */
export function SheetForm({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) {
  return <form className={cn('flex min-h-0 flex-1 flex-col', className)} {...props} />;
}

/** The fields. The only part that scrolls. */
export function SheetBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-4', className)}
      {...props}
    />
  );
}

/**
 * Save and Cancel, always on screen.
 *
 * The bottom padding is the larger of the usual 12px and the home-indicator
 * inset, so on an iPhone the buttons sit above the bar rather than under it.
 */
export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-card px-5 py-3 pb-[max(0.75rem,var(--safe-bottom))]',
        className,
      )}
      {...props}
    />
  );
}
